import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { 
  Plus, 
  Edit2, 
  Trash2,
  Layers,
  Calendar,
  Check,
  AlertCircle,
  Download,
  FileText,
  FileSpreadsheet,
  Loader2,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Seasons() {
  const { user, updateUser, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await axios.get(`${API}/seasons`);
      setSeasons(response.data);
    } catch (error) {
      console.error('Failed to fetch seasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.start_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingSeason) {
        await axios.put(`${API}/seasons/${editingSeason.id}`, formData);
        toast.success('Season updated successfully');
      } else {
        await axios.post(`${API}/seasons`, formData);
        toast.success('Season created successfully');
        await fetchUser();
      }
      fetchSeasons();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save season');
    }
  };

  const handleDelete = async (seasonId) => {
    if (!window.confirm('Are you sure? This will delete all players, matches, and trainings for this season.')) return;
    
    try {
      await axios.delete(`${API}/seasons/${seasonId}`);
      toast.success('Season deleted successfully');
      fetchSeasons();
      await fetchUser();
    } catch (error) {
      toast.error('Failed to delete season');
    }
  };

  const handleSetCurrent = async (seasonId) => {
    try {
      await axios.put(`${API}/settings/current-season/${seasonId}`);
      toast.success('Current season updated');
      await fetchUser();
    } catch (error) {
      toast.error('Failed to set current season');
    }
  };

  const handleClearCurrent = async () => {
    if (!window.confirm('Deselect the active season? The app will fall back to your account defaults until you pick a season again.')) return;
    try {
      await axios.delete(`${API}/settings/current-season`);
      toast.success('Active season deselected — using account defaults');
      await fetchUser();
    } catch (error) {
      toast.error('Failed to deselect season');
    }
  };

  const handleEdit = (season) => {
    navigate(`/seasons/${season.id}/edit`);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      start_date: '',
      end_date: ''
    });
    setEditingSeason(null);
    setDialogOpen(false);
  };

  const handleExportCSV = async (seasonId, seasonName) => {
    setExporting(`csv-${seasonId}`);
    try {
      const response = await axios.get(`${API}/reports/season/${seasonId}/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${seasonName.replace(/\//g, '-')}_Report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV report downloaded');
    } catch (error) {
      toast.error('Failed to export CSV report');
      console.error('Export error:', error);
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async (seasonId, seasonName) => {
    setExporting(`pdf-${seasonId}`);
    try {
      const response = await axios.get(`${API}/reports/season/${seasonId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${seasonName.replace(/\//g, '-')}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to export PDF report');
      console.error('Export error:', error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="seasons-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Seasons</h1>
          <div className="flex items-center gap-2">
            <Link to="/season-overview">
              <Button 
                variant="outline"
                className="rounded-xl bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                data-testid="view-overview-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">View Overview</span>
              </Button>
            </Link>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  size="icon"
                  onClick={() => resetForm()}
                  className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                  data-testid="add-season-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold font-title">
                  {editingSeason ? 'Edit Season' : 'Create New Season'}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {editingSeason ? 'Edit season details' : 'Create a new season for your team'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Season Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="2024/25 Season"
                    className="rounded-xl"
                    data-testid="season-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="rounded-xl"
                      data-testid="season-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="rounded-xl"
                      data-testid="season-end-date-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-xl">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl" data-testid="save-season-btn">
                    {editingSeason ? 'Update' : 'Create'} Season
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Current Season Info */}
        {user?.current_season_id && (
          <Card className="border-2 border-emerald-200 bg-emerald-50" data-testid="current-season-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                    <Check className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-emerald-600 font-medium">Current Season</p>
                    <p className="text-xl font-bold text-gray-900 truncate">
                      {seasons.find(s => s.id === user.current_season_id)?.name || 'Loading...'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearCurrent}
                  className="rounded-xl text-gray-600"
                  data-testid="deselect-current-season-btn"
                >
                  Deselect season
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Seasons List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading seasons...</div>
        ) : seasons.length === 0 ? (
          <div className="text-center py-16">
            <div className="empty-state-icon">
              <Layers className="w-10 h-10" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Seasons Yet</h3>
            <p className="text-gray-500 mb-6">Create your first season to start managing your team.</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="btn-primary rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Create Season
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {seasons.map((season) => (
              <Card 
                key={season.id} 
                className={`card-hover ${user?.current_season_id === season.id ? 'ring-2 ring-emerald-500' : ''}`}
                data-testid={`season-card-${season.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        user?.current_season_id === season.id ? 'bg-emerald-600' : 'bg-gray-100'
                      }`}>
                        <Layers className={`w-6 h-6 ${
                          user?.current_season_id === season.id ? 'text-white' : 'text-gray-500'
                        }`} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{season.name}</h3>
                        {user?.current_season_id === season.id && (
                          <span className="text-xs text-emerald-600 font-medium">Active</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Calendar className="w-4 h-4" strokeWidth={1.5} />
                    <span>{formatDateUK(season.start_date)}</span>
                    {season.end_date && (
                      <>
                        <span>-</span>
                        <span>{formatDateUK(season.end_date)}</span>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {user?.current_season_id !== season.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetCurrent(season.id)}
                        className="flex-1 rounded-lg"
                        data-testid={`set-current-${season.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        Set Active
                      </Button>
                    )}
                    
                    {/* Export Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          disabled={exporting?.includes(season.id)}
                          data-testid={`export-season-${season.id}`}
                        >
                          {exporting?.includes(season.id) ? (
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                          ) : (
                            <Download className="w-4 h-4" strokeWidth={1.5} />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem 
                          onClick={() => handleExportCSV(season.id, season.name)}
                          className="cursor-pointer"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" strokeWidth={1.5} />
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleExportPDF(season.id, season.name)}
                          className="cursor-pointer"
                        >
                          <FileText className="w-4 h-4 mr-2 text-red-600" strokeWidth={1.5} />
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(season)}
                      className="rounded-lg"
                      data-testid={`edit-season-${season.id}`}
                    >
                      <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(season.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                      data-testid={`delete-season-${season.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
